import * as React from "react";

import "./general-tab.scss";

import {cst, cstAssign}          from "@Common/react/chain-services";
import {consumeContext}          from "@Common/react/context";
import {scopedState}             from "@Common/react/scoped-state";
import {UseState}                from "@Common/react/use-state";
import {Types}                   from "@Common/types";
import {Responsive}              from "@Common/ui-components/responsive";
import {addURLParameterToWindow} from "@Common/utils/add-url-parameter";
import {
    filterKeys, filterValues, isEmpty
}                                from "@Common/utils/objects";

import {ModalLayout}             from "@Xpresso/routes/common/modal-layout/modal-layout";

import {FIELD_NAMES, 
        GeneralFormState} from "../../../common/general-form-state";
import {OfficeCreateTabs,
        controlInput}             from "../../office-create-tabs";

import {FloatingInput} from "@Xpresso/routes/account/common/floating-labels/floating-input";

export const GeneralTab = ({step, modal, office, dataUpdated} : {
    step: Types.Common.React.UseState.UseStateNext<string>, 
    modal: Types.Common.UiComponents.ModalService3.ModalService3Next<null>, 
    office: Types.Xpresso.Account.Admin.OfficeManagement.CreateModal.OfficeState,
    dataUpdated: Types.Common.React.UseState.UseStateNext<boolean>,
}) => cst<{
    apis        : Types.Apis.Loaders.XpressoLoadApis,
    themeScheme ?: any, 
    initialState: Types.Xpresso.Account.Admin.OfficeManagement.Common.General.FormModel,
    formState   : Types.Xpresso.Account.Admin.OfficeManagement.Common.General.FormStateNext,
    responsive  : any,
    showErrors  : Types.Common.React.UseState.UseStateNext<boolean>,
    officeFields: Array<{
        label: string;
        mapping: typeof FIELD_NAMES[0];
        maxLength: number;
    }>,
}>(
    consumeContext("apis"),
    consumeContext("themeScheme"),
    // @ts-ignore
    ["createOfficeBrandedLogin", ({themeScheme}, next) => next(themeScheme?.type === "limitedGuest")],
    cstAssign("initialState", ({}, next) => next(
        // @ts-ignore
        scopedState(office, ['inputs', 'general']).value ?? {}
    )),
    cstAssign("formState", ({initialState}, next) => GeneralFormState({branch: initialState, next})),
    cstAssign("showErrors", (_, next) => UseState({initValue: false, next})),
    cstAssign("officeFields", (_, next) => next(getOfficeFields())),
    cstAssign("responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>),
    ({formState, responsive, showErrors, createOfficeBrandedLogin}, next) => (
        ModalLayout({
            title: "Create New Office",
            content: (
                <>
                    {responsive.ge("md") && OfficeCreateTabs({step})}
                    {next()}
                </>
            ),
            buttons: [
                {
                    label: "Cancel",
                    onClick: () => {
                        modal.close();
                    },
                },
                {
                    label: "Next",
                    primary: true,
                    disabled: hasAPIError({office}),
                    className: !formState.validate() ? "disabled" : "",
                    onClick: async () => {
                        if (formState.validate()) {
                            await scopedState(office, ['inputs', 'general']).onChange(formState.value);
                            const nextStep = createOfficeBrandedLogin ? "office-address" : "login"
                            addURLParameterToWindow({parameter: "step", value: nextStep});
                            step.onChange(nextStep);
                        } else if (!showErrors.value) {
                            showErrors.onChange(true);
                        }
                    },
                },
            ]
        })
    ),
    ({officeFields, formState, showErrors}) => (
        <div className="general-tab-t56">
            {officeFields.map((officeField) => cst<{
                field: Types.Xpresso.Account.Admin.OfficeManagement.Common.General.Field,
                errorMessage: string,
            }>(
                cstAssign("field", ({}, next) => next(formState.field(officeField.mapping))),
                cstAssign("errorMessage", ({field}, next) => next(
                    (showErrors.value && field.getError()?.getMessage()) ? (
                        field.getError()?.getMessage()
                    ) : (
                        office.value.errors[officeField.mapping]
                    )
                )),
                ({field, errorMessage}) => {
                    const props = {
                        value: field.value || controlInput,
                        onChange: (e) => {
                            const value = e.target.value; 
                            scopedState(office, ['errors', officeField.mapping]).onChange(false);
                            dataUpdated.onChange(true);
                            field.onChange(value);
                        },
                        className: "value",
                        maxLength: officeField.maxLength,
                        "xd-qa-type": "textbox",
                        "xd-qa-name": officeField.label,
                    }
                    return(
                        <React.Fragment key={officeField.mapping}>
                            <FloatingInput
                                classContainer="input-card"
                                classLabel="label"
                                textLabel={officeField.label}
                                props={props}
                                required={requiredFields.includes(officeField.mapping)}
                            />

                            {errorMessage && (
                                <div className="errorMessage" key={officeField.mapping + 2}>{errorMessage}</div>
                            )}
                        </React.Fragment>
                    )
                }
            ))}
        </div>
    )
);

const requiredFields = [
    "branchName",
    "branchEmail"
];

export const getOfficeFields = ():Array<{
    label: string;
    mapping: typeof FIELD_NAMES[0];
    maxLength: number;
}> => ([
    {
        "label": "Office Name", 
        "mapping": "branchName", 
        "maxLength": 80
    },
    {
        "label": "Manager Name", 
        "mapping": "branchManagerName", 
        "maxLength": 40
    },
    {
        "label": "Office Phone 1",
        "mapping": "branchPhone1",
        "maxLength": 25
    },
    {
        "label": "Office Phone 2",
        "mapping": "branchPhone2", 
        "maxLength": 25
    },
    {
        "label": "Office Email",
        "mapping": "branchEmail",
        "maxLength": 60
    },
    {
        "label": "Admin 1 Name",
        "mapping": "branchAdmin1Name",
        "maxLength": 40
    },
    {
        "label": "Admin 1 Email",
        "mapping": "branchAdmin1Email",
        "maxLength": 60
    },
    {
        "label": "Admin 2 Name",
        "mapping": "branchAdmin2Name",
        "maxLength": 40
    },
    {
        "label": "Admin 2 Email",
        "mapping": "branchAdmin2Email",
        "maxLength": 60
    },
    {
        "label": "Office Link ID",
        "mapping": "branchLinkId",
        "maxLength": 60
    },
]);

const hasAPIError = ({office}) => {
    const generalErrors = filterKeys(scopedState(office, ['errors']).value, (error:typeof FIELD_NAMES[0]) => (
        getOfficeFields().map(a => a.mapping).includes(error)
    ));
    return !isEmpty(filterValues(generalErrors, v => v))
};
